import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { breadcrumbsActions } from '@store/breadcrumbs';
import ApplicationPageTemplate from '../../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import { CardHeader, Grid } from '@mui/material';
import { getMainDashboard } from '@store/monitoring-dashboard';
import RefreshIcon from '@mui/icons-material/Refresh';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import styles from './MainDashboard.module.scss';
import ValueCard from '@components/ValueCard';
import CartIcon1 from '@assets/images/monitoring-dashboard/cart-icon-1.png';
import LogsIcon1 from '@assets/images/monitoring-dashboard/logs-icon-1.png';
import ErrorsIcon1 from '@assets/images/monitoring-dashboard/errors-icon-1.png';
import LogDistributionTable from './components/LogDistributionTable';
import LogsTrend from './components/LogsTrend';
import NewOrdersDistributionByLOBValueCards from './components/NewOrdersDistributionByLOBValueCards';
import TopClassesLogErrors from './components/TopClassesLogErrors';
import ErrorsTrend from './components/ErrorsTrend';
import LastDetectedErrors from './components/LastDetectedErrors';
import OrderPaymentCurrencyDistribution from './components/OrderPaymentCurrencyDistribution';
import OrderPaymentFailureReasonDistribution from './components/OrderPaymentFailureReasonDistribution';
import OrderPaymentStatusDistribution from './components/OrderPaymentStatusDistribution';
import OrderPaymentMethodDistribution from './components/OrderPaymentMethodDistribution';
import { LoadingButton } from '@mui/lab';
import TrendLineByLOB from './components/TrendLineByLOB';
import TrendLine from './components/TrendLine';
import OrdersByCurrency from './components/OrdersByCurrency';
import MySQLSlowQueriesLogs from './components/MySQLSlowQueriesLogs';
import TrendLineMultiDS from './components/TrendLineMultiDS';

export default function MainDashboard() {
  const dispatch = useDispatch();
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  const mainDashboard = useSelector(
    (state) => state.monitoringDashboard.mainDashboard,
  );

  useEffect(() => {
    dispatch(
      breadcrumbsActions.setBreadcrumbs([
        { label: 'Home', url: '/' },
        { label: 'Monitoring Dashboard', url: '/monitoring-dashboard' },
        {
          label: 'Main Dashboard',
          url: '/monitoring-dashboard/main-dashboard',
        },
      ]),
    );
  });

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    setIsFetchingData(true);
    dispatch(getMainDashboard()).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch]);

  const refreshDashboard = () => {
    setIsRefreshingData(true);
    dispatch(getMainDashboard()).then(() => {
      setIsRefreshingData(false);
    });
  };

  return (
    <ApplicationPageTemplate
      title="Main Dashboard"
      description="Online monitoring for internal activities of the different system components, the dashboard focus on application level data"
      pageReady={!isFetchingData}
      toolbar={
        <>
          <LoadingButton
            loading={isRefreshingData}
            loadingPosition="start"
            variant="contained"
            onClick={refreshDashboard}
            startIcon={<RefreshIcon />}>
            Refresh
          </LoadingButton>
        </>
      }>
      {!isFetchingData && (
        <Grid
          container
          spacing={0}
          rowSpacing={1}
          columnSpacing={1}
          style={{}}>
          <Grid
            item
            xs={2}>
            <ValueCard
              label="Total Logs"
              icon={LogsIcon1}
              value={
                mainDashboard.logDistribution.count > 100000
                  ? (mainDashboard.logDistribution.count / 1000000).toFixed(2) +
                    ' M'
                  : mainDashboard.logDistribution.count
              }
            />
          </Grid>
          <Grid
            item
            xs={2}>
            <ValueCard
              label="Total Errors"
              icon={ErrorsIcon1}
              value={mainDashboard.errorLogsDistribution.count}
            />
          </Grid>
          <Grid
            item
            xs={2}>
            <ValueCard
              label="Total Orders"
              icon={CartIcon1}
              value={mainDashboard.newOrdersDistribution.count}
            />
          </Grid>
          <Grid
            item
            xs={2}>
            <ValueCard
              label="Payment Submitted"
              icon={CartIcon1}
              value={
                mainDashboard.orderSubmittionDistribution.count +
                ' (' +
                Math.round(
                  (mainDashboard.orderSubmittionDistribution.count /
                    mainDashboard.newOrdersDistribution.count) *
                    100,
                ) +
                '%)'
              }
            />
          </Grid>
          <Grid
            item
            xs={2}>
            <ValueCard
              label="Payment Succeeded"
              icon={CartIcon1}
              value={
                mainDashboard.sucessfulOrderTrend.count +
                ' (' +
                Math.round(
                  (mainDashboard.sucessfulOrderTrend.count /
                    mainDashboard.newOrdersDistribution.count) *
                    100,
                ) +
                '%)'
              }
            />
          </Grid>
          <Grid
            item
            xs={2}></Grid>
          <Grid
            item
            xs={4}>
            <Card
              sx={{}}
              className={styles.GridBlockCard}>
              <CardHeader title="Top Loggers (last 24 Hours)" />
              <CardContent style={{ height: '300px' }}>
                <LogDistributionTable
                  data={mainDashboard.logsGroupingByLogger}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={8}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Logs Trend" />
              <CardContent style={{ height: '300px' }}>
                <LogsTrend logDistribution={mainDashboard.logDistribution} />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={4}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Created Orders Trends" />
              <CardContent style={{ height: '285px' }}>
                <TrendLine data={mainDashboard.newOrdersDistribution} />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={8}>
            <NewOrdersDistributionByLOBValueCards
              data={mainDashboard.newOrdersDistributionByLOB}
            />
          </Grid>
          <Grid
            item
            xs={12}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Created Orders Trends By LOB" />
              <CardContent style={{ height: '300px' }}>
                <TrendLineMultiDS
                  data={mainDashboard.newOrdersDistributionByLOB}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={4}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Orders Submittion Trend" />
              <CardContent style={{ height: '285px' }}>
                {mainDashboard.orderSubmittionDistribution && (
                  <TrendLine data={mainDashboard.orderSubmittionDistribution} />
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={8}>
            <NewOrdersDistributionByLOBValueCards
              data={mainDashboard.orderSubmittionDistributionByLOB}
            />
          </Grid>
          <Grid
            item
            xs={12}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Orders Submittion Trend By LOB" />
              <CardContent style={{ height: '300px' }}>
                <TrendLineByLOB
                  data={mainDashboard.orderSubmittionDistributionByLOB}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={4}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Successful Orders Trend" />
              <CardContent style={{ height: '285px' }}>
                <TrendLine data={mainDashboard.sucessfulOrderTrend} />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={8}>
            <NewOrdersDistributionByLOBValueCards
              data={mainDashboard.sucessfulOrderTrendByLOB}
            />
          </Grid>
          <Grid
            item
            xs={12}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Successful Orders Trend By LOB" />
              <CardContent style={{ height: '300px' }}>
                <TrendLineByLOB data={mainDashboard.sucessfulOrderTrendByLOB} />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}>
            <Grid
              container
              spacing={0}
              rowSpacing={1}
              columnSpacing={1}
              style={{}}>
              <Grid
                item
                xs={8}>
                <Card
                  sx={{}}
                  className={styles.GridBlockCard}>
                  <CardHeader title="Successful Orders By Currency" />
                  <CardContent style={{ height: '647px' }}>
                    <OrdersByCurrency
                      data={mainDashboard.sucessfullOrdersByCurrency}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid
                item
                xs={4}>
                <Grid
                  container
                  spacing={0}
                  rowSpacing={1}
                  columnSpacing={1}
                  style={{}}>
                  <Grid
                    item
                    xs={12}>
                    <Card className={styles.GridBlockCard}>
                      <CardHeader title="Orders Payment Currency Distribution" />
                      <CardContent>
                        <OrderPaymentCurrencyDistribution
                          data={mainDashboard.paymentCurrency}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}>
                    <Card className={styles.GridBlockCard}>
                      <CardHeader title="Orders Payment Method Distribution" />
                      <CardContent>
                        <OrderPaymentMethodDistribution
                          data={mainDashboard.paymentMethod}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}>
                    <Card className={styles.GridBlockCard}>
                      <CardHeader title="Orders Payment Status Distribution" />
                      <CardContent>
                        <OrderPaymentStatusDistribution
                          data={mainDashboard.paymentSubmitsStatus}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Orders Payment Failure Reason Distribution" />
              <CardContent style={{ height: '600px' }}>
                <OrderPaymentFailureReasonDistribution
                  data={mainDashboard.paymentFailureReasonData}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Order Fetch Source Trend" />
              <CardContent style={{ height: '285px' }}>
                <TrendLineMultiDS data={mainDashboard.orderFetchSourceTrend} />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={4}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Top Classes Log Errors" />
              <CardContent style={{ height: '300px' }}>
                <TopClassesLogErrors
                  data={mainDashboard.errorLogsGroupingByLogger}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={8}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Errors Trend" />
              <CardContent style={{ height: '300px' }}>
                <ErrorsTrend
                  errorlogDistribution={mainDashboard.errorLogsDistribution}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Last Reported MySQL Slow Queries Logs" />
              <CardContent style={{ height: '800px' }}>
                <MySQLSlowQueriesLogs
                  data={mainDashboard.lastSlowQueriesList}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}>
            <Card className={styles.GridBlockCard}>
              <CardHeader title="Last Reported Errors" />
              <CardContent style={{ height: '1000px' }}>
                <LastDetectedErrors data={mainDashboard.lastErrorsList} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </ApplicationPageTemplate>
  );
}
